/**
 * Backend API client for the AI Shipping Strategy Hub.
 *
 * Wraps all `/api/v1` endpoints with typed request/response handling.
 * Uses the native `fetch` API exclusively — no external HTTP dependencies.
 *
 * This module is environment-agnostic: it works in browsers, service workers
 * (Chrome extension background), and Node 18+ (for testing).
 */

import type {
  AnalysisTask,
  MacroAnalysis,
  MicroAnalysis,
  ParsedOrder,
  PaymentOrder,
  StationAlert,
  ExportConfig,
  WechatNativeOrderCreateRequest,
} from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Response Envelope
// ---------------------------------------------------------------------------

/** Standard API response wrapper used by all endpoints. */
export interface ApiResponse<T> {
  /** Business-level status code (0 = success, non-zero = error). */
  code: number;
  /** Human-readable message (Chinese or English depending on server config). */
  message: string;
  /** Response payload. */
  data: T;
  /** Optional pagination / count metadata. */
  meta?: {
    total?: number;
    page?: number;
  };
}

// ---------------------------------------------------------------------------
// Error Handling
// ---------------------------------------------------------------------------

/** Thrown when an API request fails at the HTTP or business-logic level. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly businessCode?: number,
    public readonly responseBody?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Internal helper that executes a fetch request and unwraps the
 * `ApiResponse<T>` envelope, throwing `ApiError` on any failure.
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...options.headers,
      },
    });
  } catch (err) {
    throw new ApiError(
      `Network error: ${err instanceof Error ? err.message : String(err)}`,
      0,
    );
  }

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text().catch(() => null);
    }
    throw new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      undefined,
      body,
    );
  }

  const envelope = (await response.json()) as ApiResponse<T>;

  if (envelope.code !== 0) {
    throw new ApiError(
      envelope.message || `Business error code ${envelope.code}`,
      response.status,
      envelope.code,
      envelope,
    );
  }

  return envelope;
}

/**
 * Internal helper for requests that return a raw Blob instead of JSON
 * (e.g. file downloads).
 */
async function requestBlob(
  path: string,
  options: RequestInit = {},
): Promise<Blob> {
  const url = `${API_BASE}${path}`;

  let response: Response;
  try {
    response = await fetch(url, options);
  } catch (err) {
    throw new ApiError(
      `Network error: ${err instanceof Error ? err.message : String(err)}`,
      0,
    );
  }

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text().catch(() => null);
    }
    throw new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      undefined,
      body,
    );
  }

  return response.blob();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Upload an Excel/CSV file to create a new analysis task.
 *
 * The server will return immediately with a task ID; the actual
 * analysis runs asynchronously.
 */
export async function uploadFile(
  file: File,
): Promise<ApiResponse<AnalysisTask>> {
  const formData = new FormData();
  formData.append('file', file);

  return request<AnalysisTask>('/tasks', {
    method: 'POST',
    body: formData,
    // Do NOT set Content-Type — the browser will set it with the
    // correct multipart boundary automatically.
  });
}

/**
 * Poll the current status and progress of an analysis task.
 *
 * Clients should poll this endpoint until `status` is `completed` or `error`.
 */
export async function getTaskStatus(
  taskId: string,
): Promise<ApiResponse<AnalysisTask>> {
  return request<AnalysisTask>(`/tasks/${encodeURIComponent(taskId)}`);
}

/**
 * Retrieve the macro analysis (宏观体检) results for a completed task.
 *
 * Includes BSGD ratio, hidden surcharge totals, region & weight distributions,
 * and the stacked cost breakdown.
 */
export async function getMacroAnalysis(
  taskId: string,
): Promise<ApiResponse<MacroAnalysis>> {
  return request<MacroAnalysis>(
    `/tasks/${encodeURIComponent(taskId)}/macro`,
  );
}

/**
 * Retrieve the micro routing analysis (微观分单引擎) results for a
 * completed task.
 *
 * Includes per-order routing scores, recommendations, estimated savings,
 * and per-dimension champion summaries.
 */
export async function getMicroAnalysis(
  taskId: string,
  pageSize?: number,
): Promise<ApiResponse<MicroAnalysis>> {
  const params = pageSize ? `?page_size=${pageSize}` : '';
  return request<MicroAnalysis>(
    `/tasks/${encodeURIComponent(taskId)}/micro${params}`,
  );
}

/**
 * Retrieve station-risk alerts (末端驿站防御) for a completed task.
 *
 * Returns alerts sorted by risk level (critical first).
 */
export async function getStationAlerts(
  taskId: string,
): Promise<ApiResponse<StationAlert[]>> {
  return request<StationAlert[]>(
    `/tasks/${encodeURIComponent(taskId)}/station-alerts`,
  );
}

/**
 * Retrieve all parsed orders for a completed task.
 *
 * Returns orders in frontend `ParsedOrder` shape (camelCase, pre-mapped by backend).
 */
export async function getOrders(
  taskId: string,
): Promise<ApiResponse<ParsedOrder[]>> {
  return request<ParsedOrder[]>(
    `/tasks/${encodeURIComponent(taskId)}/orders`,
  );
}

/**
 * Download the enriched order table as an Excel or CSV file.
 *
 * The export includes the original order data plus four appended columns:
 * recommended courier, estimated savings, station-risk alert, and decision
 * rationale.
 *
 * @returns A Blob that can be saved via `URL.createObjectURL` or similar.
 */
export async function downloadExport(
  taskId: string,
  config: ExportConfig,
): Promise<Blob> {
  const params = new URLSearchParams({
    format: config.format,
    dimension: config.dimension,
    includeRationale: String(config.includeRationale),
  });

  return requestBlob(
    `/tasks/${encodeURIComponent(taskId)}/export?${params.toString()}`,
    { method: 'GET' },
  );
}

export async function createWechatNativePaymentOrder(
  payload: WechatNativeOrderCreateRequest,
): Promise<ApiResponse<PaymentOrder>> {
  return request<PaymentOrder>('/payments/wechat/native-orders', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function getPaymentOrder(
  outTradeNo: string,
): Promise<ApiResponse<PaymentOrder>> {
  return request<PaymentOrder>(
    `/payments/orders/${encodeURIComponent(outTradeNo)}`,
  );
}
